import type { FC, ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Locked } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox, DropdownButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { translate } from "app/filters/i18nFilter";
import { DROPDOWN_MODELS } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/model-dropdown/consts";
import { ModelItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/model-dropdown/ModelItem";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import {
    filterOpenModelsForUser,
    getDefaultModel,
    isAllPlatformsOptionEnabled,
    navigateToReport,
    sortModels,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { CampaignStatus, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

interface IModelDropdownProps {
    selectedCampaign: IAiTrackerCampaign;
    isDisabled: boolean;
}

export const ModelDropdown: FC<IModelDropdownProps> = ({ selectedCampaign, isDisabled }) => {
    const {
        params: { model: queryModel },
    } = useCurrentStateAndParams();

    const { currentTab } = useLLMTabs();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<LLModel>(queryModel as LLModel);
    const isDropdownDisabled = !selectedCampaign || isDisabled;
    const defaultModel = useMemo<LLModel>(
        () => getDefaultModel(selectedCampaign, currentTab.id),
        [selectedCampaign, currentTab],
    );

    const promoModels = Object.entries(DROPDOWN_MODELS)
        .filter(([, config]) => config.isPromotion)
        .map(([model]) => model) as LLModel[];

    const supportsAllPlatforms = isAllPlatformsOptionEnabled(currentTab.id);
    const supportedCampaignModels = useMemo<LLModel[]>(() => {
        return Object.entries(selectedCampaign.configs)
            .filter(([model, value]) => {
                const config = DROPDOWN_MODELS?.[model as LLModel];
                const isPending = value.status === CampaignStatus.Pending;

                return (
                    !config?.isPromotion &&
                    !config?.isNotSupported &&
                    (isPending || (value.firstScrapeDate && value.lastScrapeDate))
                );
            })
            .map(([model]) => model) as LLModel[];
    }, [selectedCampaign]);

    const availableModels = useMemo<LLModel[]>(() => {
        const openModels = filterOpenModelsForUser(supportedCampaignModels, selectedCampaign.isDemo);
        return openModels?.length
            ? sortModels(
                  openModels.filter((model) => supportsAllPlatforms || model !== LLModel.All),
                  defaultModel,
              )
            : [defaultModel];
    }, [selectedCampaign, defaultModel, supportedCampaignModels, supportsAllPlatforms]);

    const hookedModels = useMemo<LLModel[]>(() => {
        const isNTUser = isNoTouch(swSettings);

        //show all as locked for NT users
        if (isNTUser) {
            return Object.entries(DROPDOWN_MODELS)
                .filter(
                    ([model, config]) =>
                        (model as LLModel) !== LLModel.All &&
                        !config.isNotSupported &&
                        !availableModels.includes(model as LLModel),
                )
                .map(([model]) => model) as LLModel[];
        }

        return supportedCampaignModels.filter((model) => !availableModels.includes(model) && model !== LLModel.All);
    }, [supportedCampaignModels, availableModels]);

    const onModelSelection = (model: LLModel): void => {
        setIsOpen(false);

        if (model === selectedModel) {
            return;
        }

        setSelectedModel(model);
        navigateToReport(selectedCampaign, model, currentTab.id);
        allTrackers.dispatchTrackEvent("Drop Down", "click", `LLM Change/${model}`);
    };

    const openHook = (): void => {
        setIsOpen(false);
        onHookClick("AiTracker", "digitalsuite_llm_solutions_homepage", EHooksVersions.V5);
    };

    const getModelBadge = (model: LLModel): ReactElement<typeof Badge> | undefined => {
        if (model === LLModel.All) {
            return;
        }

        const status = selectedCampaign?.configs?.[model]?.status;
        if (!status) {
            return;
        }

        switch (status) {
            case CampaignStatus.Pending:
                return (
                    <Badge
                        type="custom"
                        bgColor={colorsPalettes.teal[400]}
                    >
                        {translate("llm.model_selector.badge.pending")}
                    </Badge>
                );

            case CampaignStatus.Failed:
                return (
                    <Badge
                        type="custom"
                        bgColor={colorsPalettes.red[400]}
                    >
                        {translate("llm.model_selector.badge.failed")}
                    </Badge>
                );
        }

        return;
    };

    //align with the model in the URL, and fix it if invalid for the current campaign
    useEffect(() => {
        if (queryModel && queryModel !== selectedModel) {
            if (queryModel in selectedCampaign.configs) {
                setSelectedModel(queryModel as LLModel);
            } else {
                navigateToReport(selectedCampaign, queryModel as LLModel, currentTab.id);
            }
        }
    }, [queryModel]);

    const getModelName = (model: LLModel): string => translate(DROPDOWN_MODELS[model]?.displayNameKey ?? "");

    return (
        <Combobox
            data-automation="llm-model-dropdown"
            width="200px"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={(open) => setIsOpen(open && !isDropdownDisabled)}
        >
            <Combobox.Trigger>
                <Flex width="180px">
                    <DropdownButton
                        cssClass="DropdownButton--filtersBarDropdownButton"
                        disabled={isDropdownDisabled}
                        isOpen={isOpen}
                        height={60}
                    >
                        <Box width="180px">
                            <ModelItem
                                model={selectedModel}
                                paddingLeft="0"
                            />
                        </Box>
                    </DropdownButton>
                </Flex>
            </Combobox.Trigger>
            <Combobox.Content>
                {availableModels.map((model) => (
                    <Combobox.Option
                        key={model}
                        id={model}
                        textValue={getModelName(model)}
                        onClick={() => onModelSelection(model)}
                    >
                        <ModelItem
                            model={model}
                            badge={getModelBadge(model)}
                            paddingLeft="0"
                        />
                    </Combobox.Option>
                ))}
                {hookedModels.map((model) => (
                    <Combobox.Option
                        key={model}
                        id={model}
                        textValue={getModelName(model)}
                        onClick={openHook}
                    >
                        <ModelItem
                            model={model}
                            paddingLeft="0"
                        />
                        <Locked
                            size="xs"
                            color={colorsPalettes.carbon[200]}
                        />
                    </Combobox.Option>
                ))}
                {promoModels.map((model) => (
                    <Combobox.Option
                        key={model}
                        id={model}
                        textValue={getModelName(model)}
                        isDisabled
                    >
                        <ModelItem
                            model={model}
                            badge={<Badge type="coming soon" />}
                            paddingLeft="0"
                        />
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};
